import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Upload, X, BookOpen, Music } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { daysOfWeek } from '../mock';
import { tutorService } from '../services/tutorService';
import { toast } from 'sonner';
import LocationPicker from '../components/LocationPicker';
import VideoUpload from '../components/VideoUpload';
import QualificationSelector from '../components/QualificationSelector';

const ProfileCompletion = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [tutorType, setTutorType] = useState(null); // 'academic' or 'activity'
  const [activitySkills, setActivitySkills] = useState({});
  const [formData, setFormData] = useState({
    tutor_type: null,
    boards: [], // Selected board IDs
    classes: [], // Selected class IDs
    subjects: [], // Selected subject IDs
    activity_skills: [],
    demo_video_path: null,
    education: '',
    experience_years: '',
    bio: '',
    language: '',
    hourly_rate: '',
    timezone: 'Asia/Kolkata',
    availability: [],
    service_location: '',
    service_latitude: null,
    service_longitude: null,
    service_radius_km: 10
  });

  useEffect(() => {
    loadProfile();
    loadActivitySkills();
  }, []);

  const loadProfile = async () => {
    try {
      const response = await tutorService.getProfile();
      if (response.data) {
        const profileData = {
          tutor_type: response.data.tutor_type || null,
          boards: response.data.board_ids || [],
          classes: response.data.class_ids || [],
          subjects: response.data.subject_ids || [],
          activity_skills: response.data.activity_skills || [],
          demo_video_path: response.data.demo_video_path || null,
          education: response.data.education || '',
          experience_years: response.data.experience_years || '',
          bio: response.data.bio || '',
          language: response.data.language || '',
          hourly_rate: response.data.hourly_rate || '',
          timezone: response.data.timezone || 'Asia/Kolkata',
          availability: response.data.availability || [],
          service_location: response.data.service_location || '',
          service_latitude: response.data.service_latitude || null,
          service_longitude: response.data.service_longitude || null,
          service_radius_km: response.data.service_radius_km || 10
        };
        setFormData(profileData);
        setTutorType(response.data.tutor_type);

        if (response.data.profile_image) {
          const imageUrl = response.data.profile_image.startsWith('http')
            ? response.data.profile_image
            : `${process.env.REACT_APP_BACKEND_URL || 'https://tutor.abhishekawasthi.in/public'}/${response.data.profile_image}`;
          setProfileImage(imageUrl);
        }
      }
    } catch (error) {
      console.log('No existing profile');
    }
  };

  const loadActivitySkills = async () => {
    try {
      const response = await tutorService.getActivitySkills();
      if (response.success) {
        setActivitySkills(response.data);
      }
    } catch (error) {
      console.error('Failed to load activity skills:', error);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };



  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleTutorTypeSelect = (type) => {
    setTutorType(type);
    setFormData({
      ...formData,
      tutor_type: type
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!profileImage) {
      toast.error('Please upload a Profile Photo');
      return;
    }

    if (!formData.tutor_type) {
      toast.error('Please select tutor type');
      return;
    }

    // Validate based on tutor type
    if (formData.tutor_type === 'academic') {
      if (formData.boards.length === 0) {
        toast.error('Please select at least one board');
        return;
      }
      if (formData.classes.length === 0) {
        toast.error('Please select at least one class');
        return;
      }
      if (formData.subjects.length === 0) {
        toast.error('Please select at least one subject');
        return;
      }
    } else if (formData.tutor_type === 'activity') {
      if (formData.activity_skills.length === 0) {
        toast.error('Please select at least one activity skill');
        return;
      }
      if (!formData.demo_video_path) {
        toast.error('Please upload a demo video');
        return;
      }
    }

    if (!formData.service_location || !formData.service_latitude || !formData.service_longitude) {
      toast.error('Please select your service location on the map');
      return;
    }

    setLoading(true);
    try {
      // Convert boards, classes, subjects to qualifications array
      let profileData = { 
        ...formData,
        profile_image: profileImage 
      };
      
      if (formData.tutor_type === 'academic') {
        // Create all combinations of board + class + subject (COMMENTED OUT)
        // const qualifications = [];
        // formData.boards.forEach(boardId => {
        //   formData.classes.forEach(classId => {
        //     formData.subjects.forEach(subjectId => {
        //       qualifications.push({
        //         board_id: boardId,
        //         class_id: classId,
        //         subject_id: subjectId
        //       });
        //     });
        //   });
        // });
        
        profileData = {
          ...profileData,
          // qualifications: qualifications // COMMENTED OUT
        };
        
        // The backend now relies directly on these arrays. We don't need to delete them.
        // delete profileData.boards;
        // delete profileData.classes;
        // delete profileData.subjects;
      }
      
      const response = await tutorService.saveProfile(profileData);
      toast.success('Profile saved successfully!');
      navigate('/tutor/dashboard');
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to save profile';
      toast.error(errorMessage);
      console.error('Profile save error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">

        {/* Profile Card */}
        <Card className="p-8 bg-white shadow-xl border-gray-100">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Complete Your Profile</h1>
            <p className="text-gray-600">Tell us about your teaching experience and expertise</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Tutor Type Selection */}
            {!tutorType && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-4">
                  What type of tutor are you? *
                </label>
                <div className="grid md:grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => handleTutorTypeSelect('academic')}
                    className="p-6 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all duration-200 text-left group"
                  >
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                        <BookOpen className="h-6 w-6 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-gray-900 mb-1">Academic Tutor</h3>
                        <p className="text-sm text-gray-600">
                          Teach subjects like Mathematics, Science, English, etc. for LKG to Class 12
                        </p>
                        <p className="text-xs text-gray-500 mt-2">
                          • Requires skill test • Subject & class selection
                        </p>
                      </div>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleTutorTypeSelect('activity')}
                    className="p-6 border-2 border-gray-200 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-all duration-200 text-left group"
                  >
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center group-hover:bg-purple-200 transition-colors">
                        <Music className="h-6 w-6 text-purple-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-gray-900 mb-1">Activity Tutor</h3>
                        <p className="text-sm text-gray-600">
                          Teach skills like Guitar, Dance, Yoga, Painting, Sports, etc.
                        </p>
                        <p className="text-xs text-gray-500 mt-2">
                          • Requires demo video • No skill test needed
                        </p>
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            )}

            {/* Show selected type badge */}
            {tutorType && (
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  {tutorType === 'academic' ? (
                    <>
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <BookOpen className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">Academic Tutor</p>
                        <p className="text-xs text-gray-600">Teaching academic subjects</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                        <Music className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">Activity Tutor</p>
                        <p className="text-xs text-gray-600">Teaching extracurricular activities</p>
                      </div>
                    </>
                  )}
                </div>
                <p className="text-xs text-gray-500 italic">Type cannot be changed once set</p>
              </div>
            )}

            {/* Profile Photo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-4">
                Profile Photo *
              </label>
              <div className="flex items-center space-x-6">
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-100 to-teal-100 flex items-center justify-center overflow-hidden border-4 border-white shadow-lg">
                  {profileImage ? (
                    <img src={profileImage} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <Upload className="h-10 w-10 text-blue-600" />
                  )}
                </div>
                <div>
                  <input
                    type="file"
                    id="profile-upload"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                  <label htmlFor="profile-upload">
                    <Button
                      type="button"
                      variant="outline"
                      className="border-blue-300 text-blue-700 hover:bg-blue-50 cursor-pointer"
                      onClick={() => document.getElementById('profile-upload').click()}
                    >
                      Upload Photo
                    </Button>
                  </label>
                  <p className="text-xs text-gray-500 mt-2">JPG, PNG or GIF (max 2MB)</p>
                </div>
              </div>
            </div>

            {/* Qualifications - Only for Academic Tutors */}
            {tutorType === 'academic' && (
              <QualificationSelector
                value={{ boards: formData.boards, classes: formData.classes, subjects: formData.subjects }}
                onChange={(selection) => setFormData({ 
                  ...formData, 
                  boards: selection.boards,
                  classes: selection.classes,
                  subjects: selection.subjects
                })}
              />
            )}

            {/* Activity Skills - Only for Activity Tutors */}
            {tutorType === 'activity' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Activity Skills You Can Teach * (Select multiple)
                </label>
                <p className="text-sm text-gray-500 mb-3">
                  Select the activities/skills you're qualified to teach.
                </p>
                <div className="space-y-4">
                  {Object.entries(activitySkills).map(([category, skills]) => (
                    <div key={category}>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">{category}</h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                        {skills.map((skill) => (
                          <button
                            key={skill.id}
                            type="button"
                            onClick={() => handleMultiSelect('activity_skills', skill.skill_name)}
                            className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all duration-200 ${
                              formData.activity_skills.includes(skill.skill_name)
                                ? 'bg-gradient-to-r from-purple-600 to-pink-500 border-transparent text-white shadow-md'
                                : 'border-gray-200 text-gray-700 hover:border-purple-300 hover:bg-purple-50'
                            }`}
                          >
                            {skill.skill_name}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Demo Video - Only for Activity Tutors */}
            {tutorType === 'activity' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Demo Video *
                </label>
                <p className="text-sm text-gray-500 mb-3">
                  Upload a video demonstrating your teaching style and skills (Max 15MB)
                </p>
                <VideoUpload
                  value={formData.demo_video_path}
                  onChange={(path) => setFormData({ ...formData, demo_video_path: path })}
                  maxSizeMB={15}
                />
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-6">
              {/* Education */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Education *
                </label>
                <Input
                  type="text"
                  name="education"
                  value={formData.education}
                  onChange={handleChange}
                  placeholder="e.g., B.Tech in Computer Science"
                  required
                  className="h-12 border-gray-200"
                />
              </div>

              {/* Experience */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Teaching Experience (Years) *
                </label>
                <Input
                  type="number"
                  name="experience_years"
                  value={formData.experience_years}
                  onChange={handleChange}
                  placeholder="e.g., 5"
                  required
                  min="0"
                  className="h-12 border-gray-200"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Language */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Primary Language *
                </label>
                <Input
                  type="text"
                  name="language"
                  value={formData.language}
                  onChange={handleChange}
                  placeholder="e.g., English, Hindi"
                  required
                  className="h-12 border-gray-200"
                />
              </div>

              {/* Hourly Rate */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hourly Rate (₹) *
                </label>
                <Input
                  type="number"
                  name="hourly_rate"
                  value={formData.hourly_rate}
                  onChange={handleChange}
                  placeholder="e.g., 500"
                  required
                  min="0"
                  className="h-12 border-gray-200"
                />
              </div>
            </div>

            {/* Bio */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bio / Description *
              </label>
              <Textarea
                name="bio"
                value={formData.bio}
                onChange={handleChange}
                placeholder="Tell students about your teaching philosophy, achievements, and what makes you a great tutor..."
                required
                rows={4}
                className="border-gray-200 resize-none"
              />
            </div>

            {/* Availability */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Availability (Optional)
              </label>
              <div className="flex flex-wrap gap-3">
                {daysOfWeek.map((day) => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => {
                      setFormData(prev => {
                        const availability = prev.availability || [];
                        if (availability.includes(day)) {
                          return { ...prev, availability: availability.filter(d => d !== day) };
                        }
                        return { ...prev, availability: [...availability, day] };
                      });
                    }}
                    className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all duration-200 ${
                      (formData.availability || []).includes(day)
                        ? 'bg-gradient-to-r from-blue-600 to-teal-500 border-transparent text-white shadow-md'
                        : 'border-gray-200 text-gray-700 hover:border-blue-300 hover:bg-blue-50'
                    }`}
                  >
                    {day}
                  </button>
                ))}
              </div>
            </div>

            {/* Service Location */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Service Location & Radius *
              </label>
              <LocationPicker
                value={{
                  location: formData.service_location,
                  latitude: formData.service_latitude,
                  longitude: formData.service_longitude,
                  radius: formData.service_radius_km
                }}
                onChange={(locationData) => {
                  setFormData(prev => ({
                    ...prev,
                    service_location: locationData.location,
                    service_latitude: locationData.latitude,
                    service_longitude: locationData.longitude,
                    service_radius_km: locationData.radius
                  }));
                }}
              />
            </div>

            {/* Navigation Buttons */}
            <div className="flex space-x-4 pt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/tutor/dashboard')}
                className="flex-1 h-12 border-gray-300"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 bg-gradient-to-r from-blue-600 to-teal-500 hover:from-blue-700 hover:to-teal-600 text-white h-12 shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50"
              >
                {loading ? 'Saving...' : 'Save Profile'}
              </Button>
            </div>
          </form>
        </Card>
    </div>
  );
};

export default ProfileCompletion;